import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Shield, FileSearch, Truck, FolderKanban, Settings2 } from 'lucide-react';
import { apiError } from '../lib/api';
import { toast } from 'sonner';

const LOGO_WIDE = '/logo-saga.gif';
const LOGO_CIRCLE = '/logo-saga.gif';

const features = [
  { icon: FileSearch, title: 'Licitații AI · BiddingAssistant', desc: 'Analiză caiete de sarcini, formulare, DUAE și matrice de conformitate auto-completate' },
  { icon: Truck, title: 'CRM Operațional', desc: 'Parc auto, utilaje, documente (ITP/RCA/ISCIR) și costuri — cu import automat din PDF' },
  { icon: FolderKanban, title: 'Proiecte & Contracte', desc: 'De la licitația câștigată la contract, execuție, situații de lucrări și recepție' },
  { icon: Shield, title: 'Conformitate & Termene', desc: 'Legea 98/2016, alerte pe expirări și calendar integrat' },
];

export default function LoginPage() {
  const [isRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', company_name: '', user_type: 'ofertant' });
  const { login } = useAuth();
  const [notice] = useState(() => { const value = sessionStorage.getItem('session-notice'); sessionStorage.removeItem('session-notice'); return value; });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Autentificare reusita!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      <div className="hidden lg:flex flex-1 flex-col justify-center px-16 border-r border-border">
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_WIDE} alt="SAGA Infrastructura" className="h-16 object-contain" />
          </div>
          <p className="text-xs text-primary font-medium mb-3 uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>CRM &middot; BiddingAssistant &middot; Projects</p>
          <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
            Platforma unificată pentru infrastructură rutieră: licitații, operațiuni și proiecte
          </h2>
          <p className="text-muted-foreground mb-8 leading-relaxed text-sm">
            Un singur loc pentru tot fluxul SAGA — de la câștigarea licitației cu ajutorul AI, la gestiunea parcului auto și a utilajelor, până la execuția proiectelor, contracte, costuri și conformitate. Analizează, planifică și urmărește totul cu inteligență artificială.
          </p>
          <div className="space-y-4">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-4 animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium">{f.title}</p>
                  <p className="text-xs text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_CIRCLE} alt="SAGA" className="w-10 h-10 rounded-xl object-contain" />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>SAGA</h1>
              <p className="text-[10px] text-muted-foreground">Infrastructura</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit' }}>
            {isRegister ? 'Creeaza cont' : 'Autentificare'}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {isRegister ? 'Completeaza datele pentru a crea un cont nou' : 'Introdu datele pentru a te conecta'}
          </p>

          {/* Development notice */}
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4" data-testid="login-dev-notice-banner">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                <Settings2 className="w-4 h-4 text-amber-400" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-amber-300" style={{ fontFamily: 'Outfit' }}>Platformă în curs de dezvoltare</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Această platformă se află în prezent într-o etapă activă de dezvoltare și optimizare.
                  Funcționalitățile și componentele tehnice sunt în curs de implementare, testare și validare.
                  Versiunea disponibilă nu reprezintă versiunea finală și este destinată exclusiv activităților de dezvoltare, testare și evaluare.
                </p>
                <p className="text-xs text-amber-300/80 mt-2">Vă mulțumim pentru înțelegere.</p>
              </div>
            </div>
          </div>

          {notice && <p role="alert" className="text-sm text-amber-300 mb-4" data-testid="session-revoked-notice">{notice}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label>Nume</Label>
                <Input data-testid="register-name" type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Nume complet" className="mt-1.5 bg-zinc-900/50 border-white/10" />
              </div>
            )}
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                placeholder="email@exemplu.ro" required className="mt-1.5 bg-zinc-900/50 border-white/10" />
            </div>
            <div>
              <Label>Parola</Label>
              <Input data-testid="login-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                placeholder="Parola" required className="mt-1.5 bg-zinc-900/50 border-white/10" />
            </div>
            <Link to="/forgot-password" className="block text-right text-sm text-primary hover:underline" data-testid="forgot-password-link">Am uitat parola</Link>
            <Button type="submit" className="w-full" disabled={loading} data-testid="auth-submit-btn">
              {loading ? 'Se proceseaza...' : (isRegister ? 'Inregistrare' : 'Conectare')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-6">
            SAGA INFRASTRUCTURA S.R.L. | CRM &middot; BiddingAssistant &middot; Projects
          </p>
        </div>
      </div>
    </div>
  );
}
